import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

import { CircleUserRound } from 'lucide-react';

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from '@/components/ui/pagination'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { useOperators } from '@/hooks/useApi';
import { formatDataSize, formatFrostToWal } from '@/utils/format';
import { SUI_EXPLORER_BASE_URL } from '@/lib/constants';

const PAGINATION_ITEM_COUNT = 5;
const PAGE_SIZE = 20;

const clamp = (num: number, min: number, max: number) => {
	return Math.min(Math.max(num, min), max)
}


interface OperatorsProps { data: any; totalPages: number }
function Operators(props: OperatorsProps) {
	const { data, totalPages } = props;
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [startAtPage, setStartAtPage] = useState<number>(1);

	const dataRange = useMemo(() => {
		if (!data) return []

		return data.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
	}, [data, currentPage]);

	const pageRange = useMemo(() => {
		if (!totalPages) return null;
		if (totalPages < PAGINATION_ITEM_COUNT * 2) return [...Array(totalPages).keys()].map(i => i + 1);
		if (totalPages === PAGINATION_ITEM_COUNT * 2) return [...Array(PAGINATION_ITEM_COUNT).keys()].map(i => i + startAtPage);
		if ((totalPages % PAGINATION_ITEM_COUNT) && startAtPage === totalPages - (totalPages % PAGINATION_ITEM_COUNT) + 1) {
			return [...Array(totalPages % PAGINATION_ITEM_COUNT).keys()].map(i => i + startAtPage);
		}
		return [...Array(PAGINATION_ITEM_COUNT).keys()].map(i => i + startAtPage);
	}, [currentPage, startAtPage, totalPages]);

	if (!data) return <>Loading...</>;

	return (
		<>
			<Pagination className="mb-2 border-2 border-black rounded-sm shadow-[3px_3px_0_0_black] px-2 py-1 bg-[#37C5B3]/60 backdrop-blur-md">
				<PaginationContent>
					{totalPages >= PAGINATION_ITEM_COUNT * 2 && (
						<PaginationItem>
							<PaginationPrevious
								className="bg-[#A56FF1]/40 backdrop-blur-md border-2 border-black text-black rounded-sm"
								content={<span className="text-xs">Prev</span> as any}
								onClick={() => {
									if ((currentPage - 1) % PAGINATION_ITEM_COUNT === 0) {
										setStartAtPage(clamp(currentPage - PAGINATION_ITEM_COUNT, 1, totalPages));
									}
									setCurrentPage(clamp(currentPage - 1, 1, totalPages));
								}}
							/>
						</PaginationItem>
					)}

					{totalPages > PAGINATION_ITEM_COUNT * 2 && startAtPage !== 1 && (
						<>
							<PaginationItem>
								<PaginationLink
									className="bg-[#A56FF1]/40 backdrop-blur-md border-2 border-black rounded-sm text-xs"
									isActive={1 === currentPage}
									onClick={() => {
										setCurrentPage(1);
										setStartAtPage(1);
									}}
								>1</PaginationLink>
							</PaginationItem>
							<PaginationItem><PaginationEllipsis /></PaginationItem>
						</>
					)}

					{pageRange && pageRange.map(page => (
						<PaginationItem key={page}>
							<PaginationLink
								className={cn(
									'border-2 border-black rounded-sm text-xs hover:bg-[#A56FF1]/40 hover:backdrop-blur-md',
									page === currentPage ? 'bg-[#A56FF1]/80 backdrop-blur-md' : 'bg-[#37C5B3]/5 backdrop-blur-md'
								)}
								onClick={() => setCurrentPage(clamp(page, 1, totalPages))}
							><span className='cursor-default'>{page}</span></PaginationLink>
						</PaginationItem>
					))}

					{totalPages >= PAGINATION_ITEM_COUNT * 2 && startAtPage <= totalPages - PAGINATION_ITEM_COUNT && (
						<>
							<PaginationItem><PaginationEllipsis /></PaginationItem>
							<PaginationItem>
								<PaginationLink
									className="bg-[#A56FF1]/40 backdrop-blur-md border-2 border-black rounded-sm text-xs"
									isActive={totalPages === currentPage}
									onClick={() => {
										setCurrentPage(totalPages);
										setStartAtPage(totalPages - PAGINATION_ITEM_COUNT + 1);
									}}
								>{totalPages}</PaginationLink>
							</PaginationItem>
						</>
					)}

					{totalPages >= PAGINATION_ITEM_COUNT * 2 && (
						<PaginationItem>
							<PaginationNext
								className="bg-[#C0E6FF]/10 backdrop-blur-md border-2 border-black text-black rounded-sm"
								content={<span className="text-xs">Next</span> as any}
								onClick={() => {
									if (currentPage === totalPages) return;
									if (currentPage % PAGINATION_ITEM_COUNT === 0) {
										setStartAtPage(clamp(currentPage + 1, 1, totalPages));
									}
									setCurrentPage(clamp(currentPage + 1, 1, totalPages));
								}}
							/>
						</PaginationItem>
					)}
				</PaginationContent>
			</Pagination>

			<div className="rounded-sm border-2 border-black shadow-[4px_4px_0_0_black] overflow-hidden">
				<Table>
					<TableHeader className="bg-[#37C5B3]/60 backdrop-blur-md border-b-2 border-black">
						<TableRow className='hover:bg-[#37C5B3]/60'>
							{['Operator Name', 'Operator ID', 'Stake', 'Commission', 'Capacity'].map(label => (
								<TableHead key={label} className="text-xs font-bold text-white uppercase">{label}</TableHead>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{dataRange.map((operator: any) => (
							<TableRow
								key={operator.nodeId}
								className="bg-[#37C5B3]/30 backdrop-blur-md hover:bg-[#37C5B3]/20"
							>
								<TableCell className="text-md text-bold text-white">{operator.nodeName}</TableCell>
								<TableCell className="text-md text-bold">
									<Link to={`${SUI_EXPLORER_BASE_URL}/accounts/${operator.nodeId}`} className="underline decoration-2 decoration-[#A56FF1]" target='_blank'>
										<>
											<span className="hidden md:block text-white">{`${operator.nodeId.slice(0, 6)}...${operator.nodeId.slice(-6)}`}</span>
											<span className="md:hidden text-white">{`${operator.nodeId.slice(0, 6)}...`}</span>
										</>
									</Link>
								</TableCell>
								<TableCell className="text-md text-bold text-white">{formatFrostToWal(operator.stakeFrost)}</TableCell>
								<TableCell className="text-md text-bold text-white">{Number(operator.commission) / 100}%</TableCell>
								<TableCell className="text-md text-bold text-white">{formatDataSize(Number(operator.capacityBytes))}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div >
		</>
	);
}


const OperatorsExplorePage = () => {

	const { data: operatorData } = useOperators();

	const totalPages = useMemo(() => {
		if (operatorData) {
			return Math.ceil(operatorData?.length / PAGE_SIZE);
		}
		return 0;
	}, [operatorData, PAGE_SIZE])


	return (
		<>
			<div className="flex-1 overflow-y-auto overflow-x-hidden flex max-w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
				<div className="flex flex-col w-full h-full gap-6 pr-8 lg:px-4 py-5">
					<Card className="bg-[#0C0F1D]/70 backdrop-blur-md border-2 border-black shadow-[4px_4px_0_0_black] rounded-sm">
						<CardHeader className="p-0">
							<div className="flex items-center gap-2 px-2 pt-2">
								<CircleUserRound className="h-5 w-5 text-white" />
								<CardTitle className="text-lg font-bold text-white">Operators</CardTitle>
							</div>
							<CardDescription className="text-xs px-4 pb-2 text-white">
								Current committee operators in the network
							</CardDescription>
						</CardHeader>
						<CardContent className="px-2">
							<Operators data={operatorData} totalPages={totalPages} />
						</CardContent>
					</Card>

				</div>
			</div>
		</>
	)
}

export {
	OperatorsExplorePage
}